import React from 'react';
import { Box, List, ListItem, Stack, IconButton, Chip, Divider } from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { GlossaryEntry } from './types';

interface GlossarySectionProps {
  letter: string;
  entries: GlossaryEntry[];
  onEdit: (entry: GlossaryEntry) => void;
  onDelete: (id: string) => void;
}

export const GlossarySection: React.FC<GlossarySectionProps> = ({
  letter,
  entries,
  onEdit,
  onDelete
}) => {
  if (entries.length === 0) return null;

  return (
    <Box 
      component="section" 
      id={`section-${letter}`}
      sx={{ scrollMarginTop: 2 }}
    >
      <Box
        component="h2"
        sx={{
          typography: 'h5',
          py: 2,
          px: 1,
          position: 'sticky',
          top: 0,
          bgcolor: 'background.paper',
          zIndex: 1,
          borderBottom: 1,
          borderColor: 'divider',
          m: 0
        }}
      >
        {letter}
      </Box>

      <List>
        {entries.map((entry) => (
          <React.Fragment key={entry.id}>
            <ListItem
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                py: 2
              }}
            >
              <Box sx={{ width: '100%', mb: 1 }}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="flex-start"
                  spacing={2}
                >
                  <Stack spacing={0.5}>
                    <Box component="h3" sx={{ typography: 'h6', m: 0 }}>
                      {entry.acronym}
                    </Box>
                    <Box component="div" sx={{ typography: 'subtitle1', color: 'primary.main' }}>
                      {entry.meaning}
                    </Box>
                  </Stack>
                  <Stack direction="row" spacing={1}>
                    <IconButton
                      size="small"
                      onClick={() => onEdit(entry)}
                      aria-label={`Edit ${entry.acronym}`}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => onDelete(entry.id)}
                      color="error"
                      aria-label={`Delete ${entry.acronym}`}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Stack>
                </Stack>
              </Box>

              <Box component="div" sx={{ typography: 'body2', color: 'text.secondary', mb: 1 }}>
                {entry.explanation}
              </Box>

              <Box>
                <Chip
                  label={entry.category.replace('_', ' ')}
                  size="small"
                  variant="outlined"
                />
              </Box>
            </ListItem>
            <Divider />
          </React.Fragment>
        ))}
      </List>
    </Box>
  );
};